import { useOutletContext } from "react-router-dom";

import { VolunteerResponse } from "@/api";
import { PortalAccessView } from "@/common/components/portal-access/PortalAccessView";
import { usePortalAccount } from "@/common/components/portal-access/hooks/usePortalAccount";
import { PortalAccessTexts } from "@/common/components/portal-access/portalAccess.types";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { VolunteerInvitationCard } from "./components/VolunteerInvitationCard";
import { createVolunteerPortalAccountApi } from "./volunteerPortalAccountApi";

/**
 * The volunteer entry (Zugang Ehrenamtliche) of the shared portal-access page: wires the volunteer
 * API adapter, the volunteer-specific sentences and the read-only address card into
 * PortalAccessView. No withdraw/remove capability — the adapter simply does not offer them.
 */
export function VolunteerAccess() {
  const volunteer = useOutletContext<VolunteerResponse>();
  const { t } = useTranslation();

  const texts: PortalAccessTexts = {
    title: t("volunteers.access.title"),
    description: t("volunteers.access.description"),
    pendingDescription: t("volunteers.access.pending.description"),
    confirmLockText: t("volunteers.access.manage.confirmLockText"),
    confirmUnlockText: t("volunteers.access.manage.confirmUnlockText"),
    confirmResetText: (email) => t("volunteers.access.manage.confirmResetText", { email }),
  };

  const account = usePortalAccount({
    api: createVolunteerPortalAccountApi(volunteer.volunteerId),
    statusQueryKey: [QueryKey.VolunteerPortalAccountStatus, volunteer.volunteerId],
    texts,
  });

  return (
    <PortalAccessView
      account={account}
      texts={texts}
      invitationCard={
        <VolunteerInvitationCard
          email={volunteer.email}
          onSend={() =>
            account.onSendInvitation({
              email: volunteer.email,
              identityDocumentType: null,
              identityDocumentNumber: null,
              identityDocumentIssuingAuthority: null,
              identityDocumentValidUntil: null,
            })
          }
          isSending={account.isBusy}
        />
      }
    />
  );
}
